import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import moment from 'moment';

import en from '../../resources/l10n/en.json';
import usePersistedState from '../util/usePersistedState';

// English ships in the main bundle so the interface always has strings to
// render. Every other language, and the matching moment locale, is fetched
// the first time a user picks it, which keeps 55 translation files and the
// moment locale bundle out of the initial download.
const languages = {
  af: { name: 'Afrikaans', load: () => import('../../resources/l10n/af.json').then((module) => module.default) },
  ar: { name: 'العربية', load: () => import('../../resources/l10n/ar.json').then((module) => module.default) },
  az: { name: 'Azərbaycanca', load: () => import('../../resources/l10n/az.json').then((module) => module.default) },
  bg: { name: 'Български', load: () => import('../../resources/l10n/bg.json').then((module) => module.default) },
  bn: { name: 'বাংলা', load: () => import('../../resources/l10n/bn.json').then((module) => module.default) },
  ca: { name: 'Català', load: () => import('../../resources/l10n/ca.json').then((module) => module.default) },
  cs: { name: 'Čeština', load: () => import('../../resources/l10n/cs.json').then((module) => module.default) },
  de: { name: 'Deutsch', load: () => import('../../resources/l10n/de.json').then((module) => module.default) },
  da: { name: 'Dansk', load: () => import('../../resources/l10n/da.json').then((module) => module.default) },
  el: { name: 'Ελληνικά', load: () => import('../../resources/l10n/el.json').then((module) => module.default) },
  en: { name: 'English', load: () => Promise.resolve(en) },
  es: { name: 'Español', load: () => import('../../resources/l10n/es.json').then((module) => module.default) },
  fa: { name: 'فارسی', load: () => import('../../resources/l10n/fa.json').then((module) => module.default) },
  fi: { name: 'Suomi', load: () => import('../../resources/l10n/fi.json').then((module) => module.default) },
  fr: { name: 'Français', load: () => import('../../resources/l10n/fr.json').then((module) => module.default) },
  gl: { name: 'Galego', load: () => import('../../resources/l10n/gl.json').then((module) => module.default) },
  he: { name: 'עברית', load: () => import('../../resources/l10n/he.json').then((module) => module.default) },
  hi: { name: 'हिन्दी', load: () => import('../../resources/l10n/hi.json').then((module) => module.default) },
  hr: { name: 'Hrvatski', load: () => import('../../resources/l10n/hr.json').then((module) => module.default) },
  hu: { name: 'Magyar', load: () => import('../../resources/l10n/hu.json').then((module) => module.default) },
  id: { name: 'Bahasa Indonesia', load: () => import('../../resources/l10n/id.json').then((module) => module.default) },
  it: { name: 'Italiano', load: () => import('../../resources/l10n/it.json').then((module) => module.default) },
  ja: { name: '日本語', load: () => import('../../resources/l10n/ja.json').then((module) => module.default) },
  ka: { name: 'ქართული', load: () => import('../../resources/l10n/ka.json').then((module) => module.default) },
  kk: { name: 'Қазақша', load: () => import('../../resources/l10n/kk.json').then((module) => module.default) },
  ko: { name: '한국어', load: () => import('../../resources/l10n/ko.json').then((module) => module.default) },
  km: { name: 'ភាសាខ្មែរ', load: () => import('../../resources/l10n/km.json').then((module) => module.default) },
  lo: { name: 'ລາວ', load: () => import('../../resources/l10n/lo.json').then((module) => module.default) },
  lt: { name: 'Lietuvių', load: () => import('../../resources/l10n/lt.json').then((module) => module.default) },
  lv: { name: 'Latviešu', load: () => import('../../resources/l10n/lv.json').then((module) => module.default) },
  ml: { name: 'മലയാളം', load: () => import('../../resources/l10n/ml.json').then((module) => module.default) },
  mn: { name: 'Монгол хэл', load: () => import('../../resources/l10n/mn.json').then((module) => module.default) },
  ms: { name: 'بهاس ملايو', load: () => import('../../resources/l10n/ms.json').then((module) => module.default) },
  nb: { name: 'Norsk bokmål', load: () => import('../../resources/l10n/nb.json').then((module) => module.default) },
  ne: { name: 'नेपाली', load: () => import('../../resources/l10n/ne.json').then((module) => module.default) },
  nl: { name: 'Nederlands', load: () => import('../../resources/l10n/nl.json').then((module) => module.default) },
  nn: { name: 'Norsk nynorsk', load: () => import('../../resources/l10n/nn.json').then((module) => module.default) },
  pl: { name: 'Polski', load: () => import('../../resources/l10n/pl.json').then((module) => module.default) },
  pt: { name: 'Português', load: () => import('../../resources/l10n/pt.json').then((module) => module.default) },
  ptBR: { name: 'Português (Brasil)', load: () => import('../../resources/l10n/pt_BR.json').then((module) => module.default) },
  ro: { name: 'Română', load: () => import('../../resources/l10n/ro.json').then((module) => module.default) },
  ru: { name: 'Русский', load: () => import('../../resources/l10n/ru.json').then((module) => module.default) },
  si: { name: 'සිංහල', load: () => import('../../resources/l10n/si.json').then((module) => module.default) },
  sk: { name: 'Slovenčina', load: () => import('../../resources/l10n/sk.json').then((module) => module.default) },
  sl: { name: 'Slovenščina', load: () => import('../../resources/l10n/sl.json').then((module) => module.default) },
  sq: { name: 'Shqipëria', load: () => import('../../resources/l10n/sq.json').then((module) => module.default) },
  sr: { name: 'Srpski', load: () => import('../../resources/l10n/sr.json').then((module) => module.default) },
  sv: { name: 'Svenska', load: () => import('../../resources/l10n/sv.json').then((module) => module.default) },
  ta: { name: 'தமிழ்', load: () => import('../../resources/l10n/ta.json').then((module) => module.default) },
  th: { name: 'ไทย', load: () => import('../../resources/l10n/th.json').then((module) => module.default) },
  tr: { name: 'Türkçe', load: () => import('../../resources/l10n/tr.json').then((module) => module.default) },
  uk: { name: 'Українська', load: () => import('../../resources/l10n/uk.json').then((module) => module.default) },
  uz: { name: 'Oʻzbekcha', load: () => import('../../resources/l10n/uz.json').then((module) => module.default) },
  vi: { name: 'Tiếng Việt', load: () => import('../../resources/l10n/vi.json').then((module) => module.default) },
  zh: { name: '中文', load: () => import('../../resources/l10n/zh.json').then((module) => module.default) },
  zhTW: { name: '中文 (Taiwan)', load: () => import('../../resources/l10n/zh_TW.json').then((module) => module.default) },
};

const getDefaultLanguage = () => {
  const browserLanguages = window.navigator.languages ? window.navigator.languages.slice() : [];
  const browserLanguage = window.navigator.userLanguage || window.navigator.language;
  browserLanguages.push(browserLanguage);
  browserLanguages.push(browserLanguage.substring(0, 2));

  for (let i = 0; i < browserLanguages.length; i += 1) {
    let language = browserLanguages[i].replace('-', '');
    if (language in languages) {
      return language;
    }
    if (language.length > 2) {
      language = language.substring(0, 2);
      if (language in languages) {
        return language;
      }
    }
  }
  return 'en';
};

const momentLocale = (language) => {
  if (language === 'zh') {
    return 'zh-cn';
  }
  if (language.length > 2) {
    return `${language.slice(0, 2)}-${language.slice(-2).toLowerCase()}`;
  }
  return language;
};

const LocalizationContext = createContext({
  languages,
  language: 'en',
  setLanguage: () => {},
  data: en,
});

export const LocalizationProvider = ({ children }) => {
  const [language, setLanguage] = usePersistedState('language', getDefaultLanguage());
  const [data, setData] = useState(en);
  // Bumped once the moment locale is in so consumers re-render relative times.
  const [localeVersion, setLocaleVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    languages[language].load()
      .then((loaded) => {
        if (!cancelled) {
          setData(loaded);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData(en);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  useEffect(() => {
    let cancelled = false;
    const selected = momentLocale(language);
    const apply = (locale) => {
      if (!cancelled) {
        moment.locale([locale, 'en']);
        setLocaleVersion((version) => version + 1);
      }
    };
    if (selected === 'en') {
      apply('en');
    } else {
      import(`moment/locale/${selected}`)
        .then(() => apply(selected))
        .catch(() => apply('en'));
    }
    return () => {
      cancelled = true;
    };
  }, [language]);

  const value = useMemo(
    () => ({ languages, language, setLanguage, data }),
    [language, setLanguage, data, localeVersion],
  );

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => useContext(LocalizationContext);

// A key missing from the chosen language falls back to English. Strings added
// for this deployment only exist in English and Spanish, and several callers
// run string methods on the result, so returning undefined for, say, a French
// user would throw and take the whole page down.
export const useTranslation = () => {
  const { data } = useContext(LocalizationContext);
  return useMemo(() => (key) => data[key] ?? en[key], [data]);
};

export const useTranslationKeys = (predicate) => {
  const { data } = useContext(LocalizationContext);
  return Object.keys(data).filter(predicate);
};
